import './freelancer-verification.scss';

import { ReactComponent as SuccessIcon } from '@basqet-dashboard/common/assets/icons/success.svg';
import { Button, Modal, RingLoader } from '@basqet-dashboard/common/components';
import { DeclineBanner, RestartOnboardingModal, StepNavigator } from '@components/onboarding';
import { useCompliance } from '@hooks/useCompliance';
import { canRestartProfile, useRestartOnboarding } from '@hooks/useRestartOnboarding';
import { useFreelancerStore } from '@stores/onboarding';
import { unmapFreelancerBasicDetails } from '@utils/compliance-mappers';
import {
  buildDeclinedStoreState,
  deriveStepReviewStatuses,
  isStepReadOnly
} from '@utils/section-review-status';
import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom';

import { AdditionalInformation } from './steps/additional-information';
import { BasicDetails } from './steps/basic-details';

const STEPS = [
  { id: 1, label: 'Basic Details' },
  { id: 2, label: 'Additional Information' }
];

const deriveStoreStateFromProfile = (profile, mappedStepData) => {
  const profileSteps = Array.isArray(profile?.steps) ? profile.steps : [];
  const status = profile?.status ?? profile?.kyc?.status;

  const completedFromApi = profileSteps
    .filter((step) => step.ui_status === 'COMPLETE')
    .map((step) => step.step);

  const completedStepIds =
    completedFromApi.length > 0
      ? [...new Set(completedFromApi)].sort((a, b) => a - b)
      : Object.keys(mappedStepData)
          .map(Number)
          .sort((a, b) => a - b);

  const currentFromApi = profileSteps.find((step) => step.ui_status === 'CURRENT')?.step;
  const firstIncomplete = profileSteps.find((step) => step.ui_status !== 'COMPLETE')?.step;
  const fallbackCurrent =
    completedStepIds.length > 0 ? Math.min(STEPS.length, Math.max(...completedStepIds)) : 1;

  return {
    completedStepIds,
    currentStep: currentFromApi ?? firstIncomplete ?? fallbackCurrent,
    showSuccess: status === 'PENDING' || status === 'APPROVED'
  };
};

const FreelancerVerification = () => {
  const history = useHistory();
  const currentStep = useFreelancerStore((s) => s.currentStep);
  const completedStepIds = useFreelancerStore((s) => s.completedStepIds);
  const stepData = useFreelancerStore((s) => s.stepData);
  const showSuccess = useFreelancerStore((s) => s.showSuccess);
  const pageLoading = useFreelancerStore((s) => s.pageLoading);
  const stepReviewStatuses = useFreelancerStore((s) => s.stepReviewStatuses);
  const completeStep = useFreelancerStore((s) => s.completeStep);
  const navigateToStep = useFreelancerStore((s) => s.navigateToStep);
  const setPageLoading = useFreelancerStore((s) => s.setPageLoading);
  const resetFlow = useFreelancerStore((s) => s.resetFlow);

  const { initializeKyc, loadProfile } = useCompliance();
  const {
    showRestartModal,
    isRestarting,
    setCanRestart,
    handleBackClick,
    handleRestart,
    closeRestartModal
  } = useRestartOnboarding({ resetFlow });

  useEffect(() => {
    const initialize = async () => {
      try {
        const profile = await loadProfile();

        if (profile) {
          setCanRestart(canRestartProfile(profile));
          const profileStatus = profile?.status ?? profile?.kyc?.status;

          const mappedStepData = {};
          profile.sections.forEach((section) => {
            if (section.section_key === 'FREELANCER_BASIC_DETAILS' && section.entries.length > 0) {
              const entry = section.entries[0];
              mappedStepData[1] = {
                ...unmapFreelancerBasicDetails(entry.data),
                _entryId: entry.entry_id,
                _version: entry.version
              };
            }
            if (
              section.section_key === 'FREELANCER_ADDITIONAL_INFO' &&
              section.entries.length > 0
            ) {
              const entry = section.entries[0];
              mappedStepData[2] = {
                ...entry.data,
                _entryId: entry.entry_id,
                _version: entry.version
              };
            }
          });

          const derived = deriveStoreStateFromProfile(profile, mappedStepData);

          if (profileStatus === 'DECLINED') {
            const reviewStatuses = deriveStepReviewStatuses(profile);
            if (Object.keys(reviewStatuses).length > 0) {
              useFreelancerStore.setState(
                buildDeclinedStoreState({
                  allStepIds: STEPS.map((s) => s.id),
                  reviewStatuses,
                  mappedStepData
                })
              );
            } else {
              useFreelancerStore.setState({
                stepData: mappedStepData,
                completedStepIds: derived.completedStepIds,
                currentStep: derived.currentStep,
                showSuccess: derived.showSuccess
              });
            }
          } else {
            useFreelancerStore.setState({
              stepData: mappedStepData,
              completedStepIds: derived.completedStepIds,
              currentStep: derived.currentStep,
              showSuccess: derived.showSuccess
            });
          }
        } else {
          resetFlow();
          await initializeKyc('FREELANCER');
          setCanRestart(true);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to initialize KYC:', error);
      } finally {
        setPageLoading(false);
      }
    };

    initialize();
  }, [loadProfile, initializeKyc, setPageLoading, resetFlow]);

  const handleStepComplete = (step, data) => {
    setCanRestart(true);
    completeStep(step, data);
  };

  return (
    <div className="freelancer-verification">
      <button
        type="button"
        className="freelancer-verification__back"
        disabled={pageLoading}
        onClick={handleBackClick}>
        &larr; Back
      </button>

      <div
        className="freelancer-verification__progress"
        role="progressbar"
        aria-valuemin={1}
        aria-valuemax={STEPS.length}
        aria-valuenow={currentStep}>
        {STEPS.map((step) => {
          const isActive = completedStepIds.includes(step.id) || step.id === currentStep;
          return (
            <span
              key={step.id}
              className={`freelancer-verification__progress-segment${
                isActive ? ' freelancer-verification__progress-segment--active' : ''
              }`}
            />
          );
        })}
      </div>

      <div className="freelancer-verification__body">
        <aside className="freelancer-verification__sidebar">
          <StepNavigator
            steps={STEPS}
            currentStep={currentStep}
            completedStepIds={completedStepIds}
            onStepClick={navigateToStep}
            stepReviewStatuses={stepReviewStatuses}
          />
        </aside>

        <div className="freelancer-verification__content">
          {pageLoading ? (
            <div className="freelancer-verification__loader">
              <RingLoader />
            </div>
          ) : (
            <>
              <div className="freelancer-verification__content-header">
                <h2 className="freelancer-verification__content-title">
                  {currentStep === 1 ? (
                    <>
                      Basic <span className="text-cursive">Details</span>
                    </>
                  ) : (
                    <>
                      Additional <span className="text-cursive">Information</span>
                    </>
                  )}
                </h2>
                <span className="freelancer-verification__step-count">
                  {currentStep} OF {STEPS.length}
                </span>
              </div>

              {currentStep === 1 && (
                <>
                  {stepReviewStatuses[1]?.status === 'DECLINED' && (
                    <DeclineBanner reasons={stepReviewStatuses[1].reasons} />
                  )}
                  <BasicDetails
                    onComplete={(data) => handleStepComplete(1, data)}
                    initialData={stepData[1]}
                    readOnly={isStepReadOnly(stepReviewStatuses, 1)}
                  />
                </>
              )}
              {currentStep === 2 && (
                <>
                  {stepReviewStatuses[2]?.status === 'DECLINED' && (
                    <DeclineBanner reasons={stepReviewStatuses[2].reasons} />
                  )}
                  <AdditionalInformation
                    onComplete={(data) => handleStepComplete(2, data)}
                    initialData={stepData[2]}
                    readOnly={isStepReadOnly(stepReviewStatuses, 2)}
                  />
                </>
              )}
            </>
          )}
        </div>
      </div>

      {showSuccess && (
        <Modal
          Icon={SuccessIcon}
          header={{ name: 'Thanks for submitting your KYB', cursive: 'information' }}
          onModalClose={() => history.push('/')}>
          <p className="modal__text">
            Your application is under review and we&apos;ll update you within 3 business days. You
            can continue using Basqet in Test Mode. If we need anything else, we&apos;ll reach out
            by email.
          </p>
          <Button isFull onClick={() => history.push('/')}>
            Go to Dashboard
          </Button>
        </Modal>
      )}

      <RestartOnboardingModal
        isOpen={showRestartModal}
        onClose={closeRestartModal}
        onRestart={handleRestart}
        isLoading={isRestarting}
      />
    </div>
  );
};

export { FreelancerVerification };
